// src/pages/ResetPassword.js
import React, { useMemo, useState } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import API from "../api";
import Card from "../components/ui/Card";

export default function ResetPassword() {
  const [searchParams] = useSearchParams();
  const token = useMemo(() => searchParams.get("token") || "", [searchParams]);
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const navigate = useNavigate();

  const handleSubmit = async () => {
    if (!token) {
      toast.error("Invalid reset link.", {
        className: "toast-error-custom"
      });
      return;
    }
    if (!password || password.length < 8) {
      toast.error("Password must be at least 8 characters.", {
        className: "toast-error-custom"
      });
      return;
    }
    if (password !== confirm) {
      toast.error("Passwords do not match.", {
        className: "toast-error-custom"
      });
      return;
    }
    setIsSubmitting(true);
    try {
      await API.post("/auth/reset-password", { token, newPassword: password });
      toast.success("Password updated. Please log in.", { 
        autoClose: 2500,
        className: "toast-success-custom"
      });
      setTimeout(() => navigate("/auth"), 2500);
    } catch (err) {
      console.error("Reset password error:", err);
      toast.error(
        err.response?.data?.message || "Could not reset password.",
        { 
          autoClose: 4000,
          className: "toast-error-custom"
        }
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-paper text-ink flex flex-col">
      <header className="pt-6 md:pt-10">
        <div className="max-w-md mx-auto px-4">
          <img src="/Predict90Wordmark.png" alt="Predict90" className="h-8 w-auto" />
          <h1 className="mt-6 text-[22px] md:text-[28px] font-bold text-ink">Set a new password</h1>
          <p className="mt-1 text-meta text-muted">Choose a password you don't use anywhere else.</p>
        </div>
      </header>
      <main className="flex-1 flex items-start justify-center px-4 py-6">
          <div className="w-full max-w-md">
            <Card title="Reset Password" className="w-full">
              <div className="space-y-5">
                {!token && (
                  <div className="bg-red-50 border-l-4 border-red-500 p-4 rounded">
                    <p className="text-red-800 text-sm">
                      Invalid or missing reset token. Please request a new password reset link.
                    </p>
                  </div>
                )}
                <div>
                  <label htmlFor="reset-password" className="block text-sm font-medium text-gray-700 mb-1.5">
                    New Password
                  </label>
                  <input
                    id="reset-password"
                    type="password"
                    placeholder="Enter new password (min. 8 characters)"
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand focus:border-transparent transition-all"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    disabled={isSubmitting}
                    onKeyPress={(e) => {
                      if (e.key === 'Enter' && !isSubmitting && password && confirm) {
                        handleSubmit();
                      }
                    }}
                  />
                </div>
                <div>
                  <label htmlFor="reset-confirm" className="block text-sm font-medium text-gray-700 mb-1.5">
                    Confirm Password
                  </label>
                  <input
                    id="reset-confirm"
                    type="password"
                    placeholder="Confirm your new password"
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand focus:border-transparent transition-all"
                    value={confirm}
                    onChange={(e) => setConfirm(e.target.value)}
                    disabled={isSubmitting}
                    onKeyPress={(e) => {
                      if (e.key === 'Enter' && !isSubmitting && password && confirm) {
                        handleSubmit();
                      }
                    }}
                  />
                </div>
                <button
                  onClick={handleSubmit}
                  disabled={isSubmitting || !token}
                  className={`w-full font-semibold py-3 rounded-lg transition-all flex items-center justify-center gap-2 ${
                    isSubmitting || !token
                      ? "bg-gray-300 text-gray-500 cursor-not-allowed"
                      : "bg-brand text-white hover:bg-brand-hover"
                  }`}
                >
                  {isSubmitting ? (
                    <>
                      <svg className="animate-spin h-5 w-5" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                      <span>Updating...</span>
                    </>
                  ) : (
                    "Update Password"
                  )}
                </button>
                <div className="pt-4 border-t border-gray-200">
                  <Link
                    to="/auth"
                    className="text-sm text-gray-600 hover:text-gray-900 underline text-center block"
                  >
                    Back to Sign In
                  </Link>
                </div>
              </div>
            </Card>
          </div>
        </main>

        <ToastContainer 
          position="top-center"
          autoClose={4000}
          hideProgressBar={false}
          newestOnTop={true}
          closeOnClick
          rtl={false}
          pauseOnFocusLoss
          draggable
          pauseOnHover
          theme="light"
          className="toast-container-custom"
          toastClassName="toast-custom"
          bodyClassName="toast-body-custom"
          progressClassName="toast-progress-custom"
        />
    </div>
  );
}



